import { SearchInput } from '@hulu/react-style-components';
import React, { useEffect, useMemo, useState } from 'react';

import type { TargetingPreset, TargetingRule } from '../../../apis/graphql';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import type { AdSequence, Nullable, TargetingTermValue } from '../../../models';
import { ALERT_MESSAGES, AlertFiled } from '../../../pages/Trafficking/TraffickingPage/cells/AlertCell';
import type { EntityType } from '../../../pages/Trafficking/TraffickingPage/hooks/makePageDrawerPlugin';
import type { ListHelpers } from '../../../utils/listHelpers';
import { filterTargetingTermValuesAsSet } from '../../AdForm/Targeting/util/filterTargetingTerms';
import Alert, { AlertType } from '../../Alert';
import { DEBOUNCE_DURATION_MS } from '../../hooks/constants';
import useDebounceFilter from '../../hooks/useDebounceFilter';
import TargetingDropdowns from '../../TargetingDropdowns';
import TargetingHeader from '../../TargetingHeader';
import type { TargetingHeaderClassNames } from '../../TargetingHeader/types';
import TargetingTable from '../../TargetingTable';
import type { TargetingTableRowGroup } from '../../TargetingTable/TargetingTable';
import TextViewerWrapper from '../TextViewerWrapper';
import type { TargetingScope } from '../types';

/**
 * Configuration for the dropdowns sub-section of `TargetingPanel`.
 *
 * Provided via the `dropdowns` prop. When omitted, no dropdowns are rendered
 * (read-only / display-only callers like `TargetingPresetDetails` and
 * `InventoryBlockForm` use this).
 */
export type TargetingPanelDropdowns = Readonly<{
  /** Read-only "world" the targeting is being chosen in (entity flags + lookup data). */
  scope: TargetingScope;
  /** Existing targeting term values used for include/exclude state and reset detection. */
  targetings: TargetingTermValue[];
  /**
   * List mutators for the dropdown's "include / exclude / clear" actions. Distinct
   * from the table's `listHelpers` because dropdowns also need `insertAt`.
   */
  listHelpers: Pick<ListHelpers<TargetingTermValue>, 'insertAt' | 'replaceAt' | 'removeAt'>;
  /** Validation error to surface on the Category / Type dropdowns. */
  validationError?: string;
  /** When true, dropdowns render disabled (e.g. host has its own read-only flow). */
  readonly?: boolean;
}>;

/**
 * Configuration for the search sub-section of `TargetingPanel`.
 *
 * Provided via the `search` prop. When omitted, no search input is rendered.
 *
 * The panel owns the debounced search state and recomputes a `Set<TargetingKey>`
 * which it forwards to `TargetingTable` as `filteredTargetingValues` /
 * `isTableDataFiltered`. The host does not need to wire those props itself.
 */
export type TargetingPanelSearch = Readonly<{
  /** Placeholder for the search input. Defaults to `Search Targeted Values`. */
  placeholder?: string;
  /** Optional className for the search input wrapper. */
  className?: string;
  /** Override the debounce duration (ms). Defaults to `DEBOUNCE_DURATION_MS`. */
  debounceMs?: number;
}>;

/**
 * Configuration for the inline `<TargetingHeader />` that the panel renders
 * above its dropdowns / search / table when provided.
 *
 * Hosts that previously rendered `TargetingHeader` themselves now pass the
 * same fields via `header` and stop rendering the component. When `textViewer`
 * is also provided the inline View toggle is wired up to the panel-owned
 * text-view state.
 */
export type TargetingPanelHeader = Readonly<{
  countryLabel: string;
  publisherLabel: string;
  lineItemCountryIds?: string[];
  /** Pre-formatted display string for publishers (e.g. "Hulu, Disney+"). */
  lineItemPublishers?: string;
  /**
   * When true, hides the View toggle button inside the header (separate from
   * the panel-level `editMode` which only affects the table). `DrawerTargeting`
   * sets this to the page-level `editMode`; `AdForm/Targeting` sets it to
   * `hasAdTermsChanged`.
   */
  hideViewToggleWhen?: boolean;
  classNames?: TargetingHeaderClassNames;
}>;

/**
 * Configuration for the three optional alert banners that hosts used to render
 * by hand between `TargetingHeader` and the panel body. Each truthy value
 * renders its corresponding `<Alert />`; omitted / falsy values skip silently.
 */
export type TargetingPanelAlerts = Readonly<{
  hasInvalidPublisher?: Nullable<boolean>;
  hasInvalidCountry?: Nullable<boolean>;
  adSequence?: Nullable<AdSequence>;
  classNames?: Readonly<{
    invalidPublisher?: string;
    invalidCountry?: string;
    sequenceInfo?: string;
  }>;
}>;

/**
 * Configuration for the alternate `<TextViewerWrapper />` rendering that the
 * panel toggles into when the user picks "Text View" in the header.
 *
 * Hosts that previously rendered the toggle themselves now pass the rule data
 * here and the panel owns the `textView` state internally. When this prop is
 * this prop is omitted the toggle is absent and the panel always renders the
 * table-based body.
 */
export type TargetingPanelTextViewer = Readonly<{
  entityType: EntityType.AD | EntityType.LINE_ITEM;
  adRule?: TargetingRule | null;
  lineItemRule?: TargetingRule | null;
  /**
   * When true, force the panel back to the table view regardless of the
   * user's current toggle state. `DrawerTargetingTable` sets this to its
   * page-level `editMode` so entering edit mode always returns to the table;
   * `AdForm/Targeting` leaves it unset so a user-chosen text view persists
   * across `hasAdTermsChanged` flips.
   */
  forceTableView?: boolean;
  /** Optional wrapper className applied around the rendered `TextViewerWrapper`. */
  className?: string;
}>;

export type TargetingPanelProps = Readonly<{
  // -------- table data + behavior --------

  /** Row groups passed straight through to `TargetingTable.data`. */
  data: TargetingTableRowGroup[];
  /**
   * List mutators for the table's row-level actions (delete / toggle).
   * Optional only because read-only display callers (`InventoryBlockForm`,
   * `TargetingPresetDetails`) supply trivial no-op helpers.
   */
  listHelpers?: Pick<ListHelpers<TargetingTermValue>, 'removeAt' | 'replaceAt' | 'removeAll'>;
  editMode?: boolean;
  isAd?: boolean;
  isTableToggleable?: boolean;
  /** Optional preset descriptor that swaps the action column for a read-only toggle. */
  targetingPreset?: TargetingPreset;
  /**
   * Which entity the user is editing in the surrounding host. Forwarded
   * verbatim to `TargetingTable` -> `TargetingTableActionCell`. See those
   * components for details.
   *
   * Optional only because preset-display callers (`TargetingPresetDetails`)
   * never reach the action cell.
   */
  editingEntityType?: 'ad' | 'lineItem';

  // -------- optional sub-sections --------

  /** When set, renders `<TargetingHeader />` above the alerts / dropdowns / table. */
  header?: TargetingPanelHeader;
  /** When set, renders the invalid-publisher / invalid-country / ad-sequence alerts. */
  alerts?: TargetingPanelAlerts;
  /** When set, swaps the table for `<TextViewerWrapper />` when text view is active. */
  textViewer?: TargetingPanelTextViewer;
  /** When set, renders `<TargetingDropdowns />` above the table. */
  dropdowns?: TargetingPanelDropdowns;
  /** When set, renders a debounced `<SearchInput />` above the table. */
  search?: TargetingPanelSearch;

  // -------- empty / chrome --------

  /**
   * What to render in place of the table when `data` flattens to zero
   * targeting term values. When omitted, `TargetingTable`'s built-in empty
   * state is used.
   *
   * Note: `dropdowns` (if any) and `search` (if any) still render above the
   * empty state.
   */
  emptyState?: React.ReactNode;
  /** Optional className applied to the panel root. */
  className?: string;
}>;

/**
 * `TargetingPanel` is the shared composition of `<TargetingHeader />`,
 * invalid-publisher / invalid-country / ad-sequence `<Alert />`s, the
 * `<TargetingDropdowns />` and `<SearchInput />` sub-sections, and either the
 * `<TargetingTable />` or `<TextViewerWrapper />` body. Each sub-section is
 * opt-in via its own sub-prop bundle (`header`, `alerts`, `dropdowns`,
 * `search`, `textViewer`); hosts that need a subset still only pass what they
 * use.
 *
 * Callers:
 *
 *  - `common/AdForm/Targeting/Targeting.tsx`
 *  - `pages/Trafficking/TraffickingPageDrawer/MultipleEditDrawer/.../cardsContent/Targeting/Targeting.tsx`
 *  - `pages/Trafficking/DrawerTargetingTable/Body/DrawerTargetingBody.tsx`
 *  - `common/InventoryBlockForm/InventoryBlockForm.tsx` and
 *    `common/TargetingPresetModal/TargetingPresetDetails/TargetingPresetDetails.tsx`
 *    (read-only `TargetingTable`-only consumers — table-only, no chrome)
 *
 * The panel owns the debounced search state, the `textView` toggle state (when
 * `textViewer` is provided), and the source-of-truth for "which body do we
 * "which body do we render right now". Hosts retain ownership of Formik /
 * data plumbing and the multi-edit card's outer title + mismatch warning.
 */
function TargetingPanel({
  data,
  listHelpers,
  editMode,
  isAd,
  isTableToggleable,
  targetingPreset,
  editingEntityType,
  header,
  alerts,
  textViewer,
  dropdowns,
  search,
  emptyState,
  className,
}: TargetingPanelProps): React.JSX.Element {
  const flags = useFeatureFlags();
  const [textView, setTextView] = useState(false);

  const { filterValue, debouncedFilterValue, onChange, onClear } = useDebounceFilter(
    search?.debounceMs ?? DEBOUNCE_DURATION_MS
  );

  // Flatten once so the search filter, the empty-state check, and the
  // count-changed effect all see the same source-of-truth list.
  const flatTargetingTermValues: TargetingTermValue[] = useMemo(
    () => data.flatMap((group) => group.targetingTermValues),
    [data]
  );

  const filteredTargetingValues: Set<string> = useMemo(
    () =>
      filterTargetingTermValuesAsSet({
        searchTerm: debouncedFilterValue,
        targetingTermValues: flatTargetingTermValues,
      }),
    // Match the host pattern (`useDebounceFilter` callers in the codebase keep
    // the array reference out of the dep list and watch `length` instead) so
    // recomputation is driven by debounce + add/remove rather than identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedFilterValue, flatTargetingTermValues.length]
  );

  // Mirrors the (formerly per-host) effect that clears the search box when
  // the underlying targeting list is mutated, so the user is never staring at
  // a stale filter for rows that no longer exist. Tracked on the flat term-
  // value count (not on `data.length`) because the per-group row arrays are
  // what actually mutate.
  useEffect(() => {
    if (search && filterValue.length) {
      onClear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flatTargetingTermValues.length]);

  // Whether to render the table chrome at all. Matches the prior per-host
  // behaviour: hosts that pass an `emptyState` want it shown when the data
  // structure has *no groups* (e.g. `DrawerTargetingBody` shows
  // "No current targeting data for this {entityType}." in that case). When
  // there ARE groups but they're individually empty, the table still renders
  // and shows its own per-section empty messages.
  const hasGroups = data.length > 0;
  const isSearchActive = !!debouncedFilterValue.length;

  // Whether the user is currently looking at the text-view alternative. We
  // gate on (a) the host opting into a text viewer at all, (b) the
  // `targetingTextViewRelease` flag, (c) the user's toggle state, and (d) the
  // host's optional `forceTableView` override (used by the drawer to drop
  // back to the table when entering edit mode).
  const showTextViewer = !!textViewer && flags.targetingTextViewRelease && textView && !textViewer.forceTableView;

  // Pick the panel body once so the JSX below stays a flat conditional rather
  // than a nested ternary. The `textViewer` truthiness check also narrows the
  // type for the props passed into `TextViewerWrapper`.
  let body: React.ReactNode;
  if (showTextViewer && textViewer) {
    body = (
      <div className={textViewer.className}>
        <TextViewerWrapper
          entityType={textViewer.entityType}
          adRule={textViewer.adRule}
          lineItemRule={textViewer.lineItemRule}
        />
      </div>
    );
  } else if (hasGroups || !emptyState) {
    body = (
      <TargetingTable
        data={data}
        listHelpers={listHelpers}
        editMode={editMode}
        isAd={isAd}
        isTableToggleable={isTableToggleable}
        targetingPreset={targetingPreset}
        editingEntityType={editingEntityType}
        isTableDataFiltered={!!search && isSearchActive}
        filteredTargetingValues={search ? filteredTargetingValues : undefined}
      />
    );
  } else {
    body = emptyState;
  }

  return (
    <div className={className}>
      {header && (
        <TargetingHeader
          countryLabel={header.countryLabel}
          publisherLabel={header.publisherLabel}
          lineItemCountryIds={header.lineItemCountryIds}
          lineItemPublishers={header.lineItemPublishers}
          textView={textViewer ? textView : undefined}
          onSetTextView={textViewer ? setTextView : undefined}
          editMode={header.hideViewToggleWhen}
          flags={flags}
          classNames={header.classNames}
        />
      )}
      {alerts?.hasInvalidPublisher && (
        <Alert
          type={AlertType.ERROR}
          bodyText={ALERT_MESSAGES[AlertFiled.InvalidPublisher]}
          className={alerts.classNames?.invalidPublisher}
        />
      )}
      {alerts?.hasInvalidCountry && (
        <Alert
          type={AlertType.ERROR}
          bodyText={ALERT_MESSAGES[AlertFiled.InvalidCountry]}
          className={alerts.classNames?.invalidCountry}
        />
      )}
      {alerts?.adSequence && (
        <Alert
          type={AlertType.INFORMATION}
          bodyText={ALERT_MESSAGES[AlertFiled.infoAdSequence]}
          className={alerts.classNames?.sequenceInfo}
        />
      )}
      {!showTextViewer && dropdowns && (
        <TargetingDropdowns
          listHelpers={dropdowns.listHelpers}
          targetings={dropdowns.targetings}
          validationError={dropdowns.validationError}
          readonly={dropdowns.readonly}
          scope={dropdowns.scope}
        />
      )}
      {!showTextViewer && search && hasGroups && (
        <SearchInput
          className={search.className}
          placeholder={search.placeholder ?? 'Search Targeted Values'}
          onInput={onChange}
          onClear={onClear}
          value={filterValue}
        />
      )}
      {body}
    </div>
  );
}

export default TargetingPanel;
